import { LogLevel, useLogger } from '@/composables/useLogger.ts'

/**
 * @description Type definition for a command handler.
 * A command handler can be either a synchronous function that returns void, or an asynchronous function that returns a Promise<void>.
 */
export type CommandHandler = ((args: string[]) => void | Promise<void>) | (() => void | Promise<void>)
const commands: Map<string, CommandHandler> = new Map<string, CommandHandler>()
const logger = useLogger('CommandSearch')

useCommandSearchService().register('log', (args: string[]) => {
  if (args[0]) window.__TITROP__.logger.setMinLevelGlobal(LogLevel.Trace)
  else window.__TITROP__.logger.resetLevels()
})

export function useCommandSearchService() {
  /**
   * @async
   * @description Handles a command input string.
   * @param input The command input string.
   * @returns {Promise<void>}
   */

  async function handle(input: string): Promise<void> {
    logger.debug('Handling command input: ' + input)
    const parts = input.split(' ')
    let command = parts[0].toLowerCase()
    if (!command) {
      logger.warn('Empty command')
      return
    }
    command = command.substring(1)
    const args = parts.slice(1)
    if (commands.has(command)) {
      const handler = commands.get(command)
      if (handler) await handler(args)
    } else {
      logger.warn(`Unknown command: ${command}`)
    }
  }

  /**
   * @description Registers a new command.
   * @param command The command name.
   * @param commandHandler The definition of the command
   * @throws {Error} If a command with the same name is already registered.
   */
  function register(command: string, commandHandler: CommandHandler): void {
    command = command.toLowerCase()
    if (commands.has(command)) logger.warn(`Command [${command}] got overriden`)
    else logger.debug(`Command [${command}] registered`)
    commands.set(command, commandHandler)
  }

  return { handle, register }
}
